import axios  from "axios";
import React, {
  ChangeEvent,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useCookies } from "react-cookie";
import { useMutation, useQuery } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { MultiValue, SingleValue } from "react-select";
import { Bounce, toast } from "react-toastify";

// export const baseUrl = "http://192.168.1.26:8000/api/v1/";
// export const fileURL = "http://192.168.1.26:8000/storage/";

export const baseUrl = "https://open-edu-back.icrcompany.net/api/v1/";
export const fileURL = "https://open-edu-back.icrcompany.net/storage/";

// export const baseUrl = "https://khrejeen-back.icrcompany.net/api/v1/";
// export const fileURL = "https://khrejeen-back.icrcompany.net/storage/";

export const useFetch = (url: string, key: string, enabled?: boolean) => {
  const [cookies] = useCookies(["token"], { doNotParse: true });

  // State to control when to enable the query
  const [isEnabled, setIsEnabled] = useState(enabled);

  // Use effect to enable the query only after initial render or when the enabled prop is true
  useEffect(() => {
    if (enabled === undefined) setIsEnabled(true);
  }, []);

  // Fetch Data Function
  const fetchData = async () => {
    const response = await axios.get(`${baseUrl}${url}`, {
      headers: {
        Authorization: `Bearer ${cookies.token}`,
        Accept: "application/json",
      },
    });
    return response.data;
  };

  // Query logic
  const query = useQuery(key, fetchData, {
    enabled: enabled === undefined ? isEnabled : enabled, // Only enable if isEnabled is true
  });

  return query;
};

export interface InputFile {
  id ?: string
  name: string;
  value: string;
  type: string;
}

type FilesState = Record<string, File>;

type CheckedArray = Record<string, string[]>;

type SelectedOption = {
  value: string;
  label: string;
};

export const usePOST = <FormDataType extends Record<string, any>>(
  initialData: FormDataType,
  actionSuccess: (data: any | undefined) => void,
  actionError: (error: any | undefined) => void
) => {
  const [cookies, setCookie] = useCookies(["token"], {
    doNotParse: true,
  });
  const [formData, setFormData] = useState<FormDataType>(initialData);
  const [files, setFiles] = useState<FilesState>({});
  
const [viewFile, setViewFile] = useState<{ [key: string]: { image?: string; pdf?: string } }>({});

  const [viewFiles, setViewFiles] = useState<InputFile[]>([]);
  const [checkedArray, setCheckedArray] = useState<CheckedArray>({});
  const [counter, setCounter] = useState(0);

  const prepareFormData = useCallback(() => {
    const formDataToSend = new FormData();

    for (const [key, value] of Object.entries(formData)) {
      formDataToSend.append(key, value as string | Blob);
    }

    if (checkedArray) {
      for (const [key, value] of Object.entries(checkedArray)) {
        for (let i = 0; i < value.length; i++) {
          formDataToSend.append(key, value[i]);
        }
      }
    }

    if (files) {
      for (const [key, value] of Object.entries(files)) {
        formDataToSend.append(key, value);
      }
    }

    return formDataToSend;
  }, [formData, checkedArray, files]);

  const mutation = useMutation(
    async (url: string) => {
      const formDataToSend = prepareFormData();
      const res = await axios.post(`${baseUrl}${url}`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${cookies.token}`,
          Accept: "application/json",
        },
      });
      return res;
    },

    {
      onSuccess: (data) => {
        actionSuccess(data);
      },
      onError: (error) => {
        actionError(error);
      },
    }
  );

  const handleSubmit = (url: string) => {
    mutation.mutate(url);
  };

  const handleChangeSelect = (selected: SelectedOption) => {
    setFormData((prev) => ({
      ...prev,
      [selected.label]: selected.value,
    }));
  };

  const handleChangeMultiSelect = (
    selected: MultiValue<SelectedOption> | SingleValue<SelectedOption>,
    active: { name: string }
  ) => {
    setFormData((prev) => {
      if (Array.isArray(selected)) {
        const selectedValues = selected.reduce((acc, option, index) => {
          acc[`${active.name}[${index}]`] = option.value;
          return acc;
        }, {} as { [key: string]: string });

        const filteredFormData = Object.keys(prev)
          .filter((key) => !key.startsWith(`${active.name}[`))
          .reduce((acc, key) => {
            acc[key] = prev[key];
            return acc;
          }, {} as { [key: string]: string });

        return {
          ...filteredFormData,
          ...selectedValues,
        };
      } else {
        return {
          ...prev,
          [`${active.name}`]: selected
            ? (selected as SingleValue<SelectedOption>)?.value
            : undefined,
        };
      }
    });
  };

  const MAX_SIZE = 2 * 1024 * 1024;

  const handleChangeInput = (event: ChangeEvent<HTMLInputElement>, id?: string) => {
    const notifyErr = () =>
      toast.error("يجب أن يكون حجم الصورة أقل من 2M", {
        position: "bottom-left",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
  
    const { name, value, type, files, checked } = event.target;
    let currentValue: string | File | boolean | undefined | number;
  
    if (type === "file") {
      let file = (currentValue = files ? files[0] : undefined);
      if (file) {
        if (file.size > MAX_SIZE) {
          notifyErr();
          event.target.value = "";
          return;
        }
        const fileType = file.type;
        const showfile = URL.createObjectURL(file as Blob | MediaSource);
  
        if (fileType.includes("image")) {
          if (id) {
            setViewFile((prev) => ({
              ...prev,
              [id]: { ...prev[id], image: showfile }, 
            }));
          }
        } else if (fileType.includes("pdf")) {
          if (id) {
            setViewFile((prev) => ({
              ...prev,
              [id]: { ...prev[id], pdf: showfile },
            }));
          }
        }
      }
      event.target.value = "";
    } else if (type === "checkbox") {
      currentValue = checked;
    } else if (type === "radio") {
      currentValue = checked ? 1 : 0;
    } else if (type === "time") {
      currentValue = `${value}:00`;
    } else {
      currentValue = value;
    }
  
    setFormData((prev) => ({ ...prev, [name]: currentValue }));
  };
  
  

  const handleChangeTextArea = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeArrayFiles = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputFiles = event.target.files;
    const newFilesObject: FilesState = {};

    if (inputFiles) {
      const currentImagesCount = Object.keys(files).length;
      for (let i = 0; i < inputFiles.length; i++) {
        newFilesObject[`images[${currentImagesCount + i}]`] = inputFiles[i];
      }

      setFiles((prevImages) => ({
        ...prevImages,
        ...newFilesObject,
      }));

      const urlImages: InputFile[] = [];
      for (let i = 0; i < inputFiles.length; i++) {
        if (!inputFiles[i]) continue;
        urlImages.push({
          name: inputFiles[i].name,
          value: URL.createObjectURL(inputFiles[i]),
          type: inputFiles[i].type,
        });
      }
      setViewFiles((prev) => [...prev, ...urlImages]);

      event.target.value = "";
    }
  };

  const handleCheckedArray = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked, name } = e.target;
    if (checked) {
      setCheckedArray((prev) => ({
        ...prev,
        [name]: prev[name] ? [...prev[name], value] : [value],
      }));
    } else {
      setCheckedArray((prev) => ({
        ...prev,
        [name]: prev[name].filter((p) => p !== value),
      }));
    }
  };

  return {
    handleSubmit,
    formData,
    setFormData,
    handleChangeInput,
    handleChangeTextArea,
    handleChangeArrayFiles,
    files,
    viewFiles,
    viewFile,
    handleCheckedArray,
    handleChangeSelect,
    mutation,
    setViewFile,
    cookies,
    setViewFiles,
    setCookie,
    setCounter,
    counter,
    setFiles,
    handleChangeMultiSelect,
  };
};

export const useDelete = (
  actionSuccess: () => void,
  actionError: () => void
) => {
  const [cookies] = useCookies(["token"], {
    doNotParse: true,
  });
  const mutation = useMutation(
    (url: string) =>
      axios
        .delete(baseUrl + url, {
          headers: {
            Authorization: "Bearer " + cookies.token,
            Accept: "application/json",
          },
        })
        .then((res) => res.data),
    {
      onSuccess: () => {
        actionSuccess();
        window.location.reload();
      },
      onError: () => {
        actionError();
      },
    }
  );

  const deleteItem = (url: string) => {
    mutation.mutate(url);
  };

  return { deleteItem, mutation };
};

export const useFilter = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(search);

  const handleParamsClick = (
    name: string,
    value: string | string[] | number
  ) => {
    if (Array.isArray(value)) {
      if (value.length > 0) searchParams.set(name, value.join(","));
      else searchParams.delete(name);
    } else if (value) {
      searchParams.set(name, value.toString());
    }
    const newUrl = `${pathname}?${searchParams.toString()}`;
    navigate(newUrl);
  };

  const handleParamDelete = (name: string) => {
    searchParams.delete(name);
    const newUrl = `${pathname}?${searchParams.toString()}`;
    navigate(newUrl);
  };

  const handleParamsDeleteAll = () => {
    const newUrl = `${pathname}`;
    navigate(newUrl);
  };

  const handlePageClick = (event: { selected: number }) => {
    handleParamsClick("page", event.selected + 1);
  };

  return {
    searchParams,
    handlePageClick,
    handleParamsClick,
    handleParamDelete,
    handleParamsDeleteAll,
  };
};

const logoutRequest = async (url: string, token: string): Promise<void> => {
  await axios.post(
    `${baseUrl}${url}`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
};

export const useLogout = (actionError: () => void) => {
  const navigate = useNavigate();
  const [cookies, setCookie, removeCookie] = useCookies(["token"], {
    doNotParse: true,
  });

  const mutation = useMutation<void, Error, string>({
    mutationFn: (url) => logoutRequest(url, cookies.token),
    onSuccess: () => {
      removeCookie("token");
      navigate("/auth/signin");
      window.location.reload();
    },
    onError: () => {
      actionError();
    },
  });
  return mutation;
};

interface UseCloseReturnType {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  mouse: React.RefObject<HTMLDivElement>;
}

export const useClose = (): UseCloseReturnType => {
  const [open, setOpen] = useState<boolean>(false);
  const mouse = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (mouse.current && !mouse.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, []);

  return { open, setOpen, mouse };
};

export const RequireAuth: React.FC<{
  children: ReactElement<any, any> | null;
}> = ({ children }) => {
  const [cookies] = useCookies(["token"], { doNotParse: true });
  const navigate = useNavigate();

  useEffect(() => {
    if (!cookies.token) {
      navigate("/login");
    }
  }, [cookies.token, navigate]);

  return children;
};

export const clickZoomInImage = (event: React.MouseEvent<HTMLImageElement>) => {
  const target = event.target as HTMLImageElement;
  const originalClassName = target.className;

  target.className = "rounded-none";

  if (target.requestFullscreen) {
    target.requestFullscreen().then(() => {
      document.addEventListener("fullscreenchange", () => {
        if (!document.fullscreenElement) {
          target.className = originalClassName;
        }
      });
    });
  }
};
